export interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId?: string;
  buildVersion?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
}

export interface NetworkErrorReport {
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  responseData?: any;
  requestData?: any;
  timestamp: string;
  userId?: string;
}

class ErrorReportingService {
  private readonly maxReports = 100;
  private reports: ErrorReport[] = [];
  private networkErrors: NetworkErrorReport[] = [];

  // Report JavaScript errors
  reportError(error: Error, context?: Record<string, any>, severity: ErrorReport['severity'] = 'medium'): void {
    const report: ErrorReport = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
      buildVersion: process.env.REACT_APP_VERSION,
      severity,
      context,
    };

    this.addReport(report);
    this.logToConsole(report);

    // In production, send to external service
    if (process.env.NODE_ENV === 'production') {
      this.sendToExternalService(report);
    }
  }

  // Report network errors
  reportNetworkError(config: {
    url: string;
    method: string;
    status?: number;
    statusText?: string;
    responseData?: any;
    requestData?: any;
  }): void {
    const report: NetworkErrorReport = {
      ...config,
      timestamp: new Date().toISOString(),
      userId: this.getCurrentUserId(),
    };

    this.addNetworkError(report);
    this.logNetworkError(report);

    // In production, send to external service
    if (process.env.NODE_ENV === 'production') {
      this.sendNetworkErrorToExternalService(report);
    }
  }

  // Report React component errors
  reportComponentError(error: Error, errorInfo: React.ErrorInfo, severity: ErrorReport['severity'] = 'high'): void {
    const report: ErrorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack || undefined,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
      buildVersion: process.env.REACT_APP_VERSION,
      severity,
      context: { type: 'component_error' },
    };

    this.addReport(report);
    this.logToConsole(report);

    if (process.env.NODE_ENV === 'production') {
      this.sendToExternalService(report);
    }
  }

  // Get all stored error reports
  getReports(): ErrorReport[] {
    return [...this.reports];
  }

  // Get all stored network error reports
  getNetworkErrors(): NetworkErrorReport[] {
    return [...this.networkErrors];
  }

  // Clear all reports
  clearReports(): void {
    this.reports = [];
    this.networkErrors = [];
  }

  private addReport(report: ErrorReport): void {
    this.reports.unshift(report);
    if (this.reports.length > this.maxReports) {
      this.reports = this.reports.slice(0, this.maxReports);
    }
  }

  private addNetworkError(report: NetworkErrorReport): void {
    this.networkErrors.unshift(report);
    if (this.networkErrors.length > this.maxReports) {
      this.networkErrors = this.networkErrors.slice(0, this.maxReports);
    }
  }

  private logToConsole(report: ErrorReport): void {
    console.group(`🚨 Error Report [${report.severity.toUpperCase()}]`);
    console.error('Message:', report.message);
    console.error('Stack:', report.stack);
    if (report.componentStack) {
      console.error('Component Stack:', report.componentStack);
    }
    console.error('Context:', report.context);
    console.error('Full Report:', report);
    console.groupEnd();
  }

  private logNetworkError(report: NetworkErrorReport): void {
    console.group(`🌐 Network Error Report`);
    console.error('URL:', report.url);
    console.error('Method:', report.method);
    console.error('Status:', report.status, report.statusText);
    console.error('Response:', report.responseData);
    console.error('Request:', report.requestData);
    console.groupEnd();
  }

  private getCurrentUserId(): string | undefined {
    // Get user ID from auth context or local storage
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user?.id;
      }
    } catch (error) {
      // Ignore parsing errors
    }
    return undefined;
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  private async sendToExternalService(report: ErrorReport): Promise<void> {
    try {
      // In a real application, replace this with your error reporting service
      // Examples: Sentry, LogRocket, Bugsnag, etc.
      
      // For now, we'll just store in localStorage as a fallback
      const storedReports = localStorage.getItem('errorReports');
      const reports = storedReports ? JSON.parse(storedReports) : [];
      reports.push(report);
      
      // Keep only the last 50 reports in localStorage
      const trimmedReports = reports.slice(-50);
      localStorage.setItem('errorReports', JSON.stringify(trimmedReports));
      
      // TODO: Replace with actual service call
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(report),
      // });
    } catch (error) {
      console.error('Failed to send error report:', error);
    }
  }

  private async sendNetworkErrorToExternalService(report: NetworkErrorReport): Promise<void> {
    try {
      const storedErrors = localStorage.getItem('networkErrors');
      const errors = storedErrors ? JSON.parse(storedErrors) : [];
      errors.push(report);
      
      const trimmedErrors = errors.slice(-50);
      localStorage.setItem('networkErrors', JSON.stringify(trimmedErrors));
    } catch (error) {
      console.error('Failed to send network error report:', error);
    }
  }
}

export const errorReportingService = new ErrorReportingService();

// Set up global error handlers
window.addEventListener('error', (event) => {
  errorReportingService.reportError(
    new Error(event.message),
    {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    },
    'high'
  );
});

window.addEventListener('unhandledrejection', (event) => {
  errorReportingService.reportError(
    new Error(`Unhandled Promise Rejection: ${event.reason}`),
    { type: 'unhandled_promise_rejection' },
    'high'
  );
});